import {
  LayoutDashboard,
  Users,
  CalendarDays,
  Clock,
  CheckSquare,
  ClipboardList,
  AlertTriangle,
  FileText,
  Receipt,
  DollarSign,
  BarChart3,
  Settings,
  Bot,
  Sparkles,
  Briefcase,
  Target,
  Laptop,
  UserMinus,
  BookUser,
  Megaphone,
  MessageSquare,
  ScrollText,
  Activity,
  GraduationCap,
  Network,
  GitFork,
  Crown,
  Wallet,
  LineChart,
  Brain,
  ShieldAlert,
  ShieldCheck,
  type LucideIcon,
} from "lucide-react";
import { Role } from "@/types/common";

export interface RouteConfig {
  path: string;
  /** English fallback label — prefer ``labelKey`` for display. */
  label: string;
  /** i18next key under the `nav` namespace (see locale files). */
  labelKey: string;
  icon: LucideIcon;
  roles: Role[];
  /**
   * Optional plan feature flag key. When set, the sidebar hides the item
   * (or shows a Pro badge, depending on mode) for orgs whose plan does
   * not include the flag. Matches the keys returned by
   * ``GET /billing/features``.
   */
  feature?:
    | "ai_agents"
    | "recruiting"
    | "performance"
    | "complaints"
    | "advanced_reports"
    | "workflows"
    | "compliance"
    | "audit_logs"
    | "sso"
    // Newly-tracked Starter+ flags. Mirror the backend's PLAN_FEATURES
    // additions so the sidebar hides these items for Free orgs (instead
    // of letting them click through to a 402-on-hit).
    | "invoices"
    | "assets"
    | "one_on_ones"
    | "ai_assistant"
    | "lifecycle"
    | "pulse_surveys"
    | "training"
    | "talent_map"
    | "succession"
    | "compensation"
    | "expenses"
    | "workforce_planning"
    | "predictive_analytics"
    | "teams_integration"
    | "sms_notifications";
  /**
   * Optional module key. When set, the sidebar hides the item for orgs
   * whose plan's ``modules`` list does not include this key. Modules
   * are coarser than features — they gate whole sections regardless of
   * per-capability flags.
   */
  module?: string;
  /**
   * Sidebar grouping. Determines which section the item lives under.
   * The ``ai_assistant`` and ``settings`` groups render at the bottom
   * of the sidebar (pinned, not part of the scrollable list).
   */
  group?:
    | "main"
    | "organization"
    | "time_and_pay"
    | "benefits"
    | "talent"
    | "insights"
    | "compliance"
    | "settings"
    | "ai_assistant"
    | "platform";
  /**
   * Platform-admin-only sidebar items. These bypass ``roles`` (since
   * customer roles like SUPER_ADMIN are unrelated to the platform-
   * admin bit) and render only when ``user.is_platform_admin`` is
   * true. Used for /admin/* routes that exist for Lenavio staff.
   */
  platformAdminOnly?: boolean;
}


/** Render order + display label for each sidebar group. ``main`` has
 *  no header (items render directly at the top). ``settings`` and
 *  ``ai_assistant`` are pinned at the bottom outside the scroll area. */
export interface NavGroup {
  id: NonNullable<RouteConfig["group"]>;
  label: string | null; // null = no header
  /** Pinned to the bottom of the sidebar instead of the scroll area. */
  pinned?: boolean;
}

export const navGroups: NavGroup[] = [
  { id: "main", label: null },
  { id: "organization", label: "Organization" },
  { id: "time_and_pay", label: "Time & Pay" },
  { id: "benefits", label: "Benefits" },
  { id: "talent", label: "Talent" },
  { id: "insights", label: "Insights" },
  { id: "compliance", label: "Compliance & Docs" },
  // Platform-admin items. Pinned to bottom, sit just above Settings.
  // Only render at all when ``user.is_platform_admin`` is true, so
  // for normal customers this group is invisible.
  { id: "platform", label: null, pinned: true },
  { id: "settings", label: null, pinned: true },
  { id: "ai_assistant", label: null, pinned: true },
];

// ─── Sidebar order is derived from THIS list, grouped by ``group``. ───
//
// Role policy (matches the product spec):
//
//   EMPLOYEE  →  Dashboard, My Team, Directory, Org Chart,
//                Announcements, Time Off, Time Tracking, My Documents,
//                AI Assistant, plus per-feature surfaces (1:1s, Pulse,
//                Performance, My Training, Expenses) when their plan
//                flag is on.
//   MANAGER   →  everything an Employee sees, plus People (the org
//                roster used to manage direct reports), 1:1s,
//                Approvals, Recruiting, Performance, Documents.
//   HR_ADMIN  →  everything except the Settings page (super-admin
//                only).
//   SUPER_ADMIN → everything, including Settings.
//
// Feature-flag visibility (``feature``) is enforced at render time by
// the Sidebar — when a plan doesn't include a feature, the row is
// either hidden (during trial, where everything's unlocked anyway) or
// shown with a "PRO" pill that links to /settings/billing.

export const navRoutes: RouteConfig[] = [
  // ── MAIN ──────────────────────────────────────────────────────────
  { path: "/", label: "Dashboard", labelKey: "nav.dashboard", icon: LayoutDashboard, roles: ["SUPER_ADMIN", "HR_ADMIN", "MANAGER", "EMPLOYEE"], group: "main" },
  // People = the org-wide HR roster. Managers see it so they can
  // reach their direct reports' profiles; employees do not (the
  // /directory list is the public colleague-finder for them).
  { path: "/employees", label: "People", labelKey: "nav.people", icon: Users, roles: ["SUPER_ADMIN", "HR_ADMIN", "MANAGER"], group: "main" },
  // My Team = the "people who report to me" Manager Team Dashboard.
  // EMPLOYEE is intentionally excluded: both the route's RoleGate
  // (App.tsx) and the /my/team backend API reject the role, so
  // advertising it in the sidebar just produced an Access Denied
  // page. EMPLOYEEs browse colleagues via Directory + Org Chart
  // below, both of which are already EMPLOYEE-allowed.
  { path: "/my/team", label: "My Team", labelKey: "nav.myTeam", icon: Users, roles: ["SUPER_ADMIN", "HR_ADMIN", "MANAGER"], group: "main" },
  // 1:1s — employees see their own meetings with their manager. Page
  // hides the "Schedule new" button for non-managers via inline
  // ``isManagerRole`` gate.
  { path: "/one-on-ones", label: "1:1s", labelKey: "nav.oneOnOnes", icon: MessageSquare, roles: ["SUPER_ADMIN", "HR_ADMIN", "MANAGER", "EMPLOYEE"], feature: "one_on_ones", group: "main" },

  // ── ORGANIZATION ──────────────────────────────────────────────────
  { path: "/directory", label: "Directory", labelKey: "nav.directory", icon: BookUser, roles: ["SUPER_ADMIN", "HR_ADMIN", "MANAGER", "EMPLOYEE"], group: "organization" },
  { path: "/employees/org-chart", label: "Org Chart", labelKey: "nav.orgChart", icon: GitFork, roles: ["SUPER_ADMIN", "HR_ADMIN", "MANAGER", "EMPLOYEE"], group: "organization" },
  { path: "/announcements", label: "Announcements", labelKey: "nav.announcements", icon: Megaphone, roles: ["SUPER_ADMIN", "HR_ADMIN", "MANAGER", "EMPLOYEE"], group: "organization" },
  // Assets — employees see only their own assigned gear. Backend
  // filters the list to ``assigned_to=me`` for non-staff and the
  // page hides admin actions (New, Archive, Assign) via inline
  // ``isAdmin`` gate.
  { path: "/assets", label: "Assets", labelKey: "nav.assets", icon: Laptop, roles: ["SUPER_ADMIN", "HR_ADMIN", "MANAGER", "EMPLOYEE"], feature: "assets", group: "organization" },
  { path: "/offboarding", label: "Offboarding", labelKey: "nav.offboarding", icon: UserMinus, roles: ["SUPER_ADMIN", "HR_ADMIN"], feature: "lifecycle", group: "organization" },

  // ── TIME & PAY ────────────────────────────────────────────────────
  { path: "/leave", label: "Time Off", labelKey: "nav.timeOff", icon: CalendarDays, roles: ["SUPER_ADMIN", "HR_ADMIN", "MANAGER", "EMPLOYEE"], group: "time_and_pay" },
  { path: "/time", label: "Time Tracking", labelKey: "nav.timeTracking", icon: Clock, roles: ["SUPER_ADMIN", "HR_ADMIN", "MANAGER", "EMPLOYEE"], group: "time_and_pay" },
  { path: "/invoices", label: "Invoices", labelKey: "nav.invoices", icon: Receipt, roles: ["SUPER_ADMIN", "HR_ADMIN", "MANAGER"], feature: "invoices", group: "time_and_pay" },
  // Expenses — employees submit their own reports here. Page tabs
  // are role-gated: "My reports" for everyone, "Approvals" for
  // managers, "Dashboard" for HR Admins. Inline ``showApprovals``
  // / ``showDashboard`` checks keep employees out of admin tabs.
  { path: "/expenses", label: "Expenses", labelKey: "nav.expenses", icon: Wallet, roles: ["SUPER_ADMIN", "HR_ADMIN", "MANAGER", "EMPLOYEE"], feature: "expenses", group: "time_and_pay" },
  { path: "/compensation", label: "Compensation", labelKey: "nav.compensation", icon: DollarSign, roles: ["SUPER_ADMIN", "HR_ADMIN"], group: "time_and_pay" },

  // ── BENEFITS ─────────────────────────────────────────────────────
  // The Lenavio Benefits module — replaces Employee Navigator. Three
  // admin surfaces (dashboard, plans, periods, payroll), one employee
  // surface (enrollment portal), and a shared life-events page that
  // renders the right view based on role. COBRA + Carrier Invoices
  // (already in /compliance) also belong logically here, but keep
  // their existing sidebar slot to avoid double-listing.
  { path: "/benefits/dashboard", label: "Benefits", labelKey: "nav.benefitsDashboard", icon: ShieldCheck, roles: ["SUPER_ADMIN", "HR_ADMIN"], group: "benefits" },
  { path: "/benefits/enrollment", label: "Enrollment", labelKey: "nav.benefitsEnrollment", icon: ClipboardList, roles: ["SUPER_ADMIN", "HR_ADMIN", "MANAGER", "EMPLOYEE"], group: "benefits" },
  { path: "/benefits/plans", label: "Plans", labelKey: "nav.benefitsPlans", icon: FileText, roles: ["SUPER_ADMIN", "HR_ADMIN"], group: "benefits" },
  { path: "/benefits/periods", label: "Enrollment Periods", labelKey: "nav.benefitsPeriods", icon: CalendarDays, roles: ["SUPER_ADMIN", "HR_ADMIN"], group: "benefits" },
  { path: "/benefits/life-events", label: "Life Events", labelKey: "nav.benefitsLifeEvents", icon: Sparkles, roles: ["SUPER_ADMIN", "HR_ADMIN", "MANAGER", "EMPLOYEE"], group: "benefits" },
  { path: "/benefits/payroll", label: "Payroll Deductions", labelKey: "nav.benefitsPayroll", icon: Wallet, roles: ["SUPER_ADMIN", "HR_ADMIN"], group: "benefits" },


  // ── TALENT ────────────────────────────────────────────────────────
  { path: "/recruiting", label: "Recruiting", labelKey: "nav.recruiting", icon: Briefcase, roles: ["SUPER_ADMIN", "HR_ADMIN", "MANAGER"], feature: "recruiting", group: "talent" },
  // Performance — employees see their own goals + reviews. Page
  // hides admin features (review cycles, calibration) via inline
  // ``isAdmin`` gate; managers gain the team-review surface.
  { path: "/performance", label: "Performance", labelKey: "nav.performance", icon: Target, roles: ["SUPER_ADMIN", "HR_ADMIN", "MANAGER", "EMPLOYEE"], feature: "performance", group: "talent" },
  // Managers can reach Pulse to send surveys to their team (the
  // "Send to all / specific people" surface). Employees don't see
  // the entry in the sidebar — they receive surveys via in-app
  // notifications and click through to /pulse/:id/respond.
  { path: "/pulse", label: "Pulse", labelKey: "nav.pulse", icon: Activity, roles: ["SUPER_ADMIN", "HR_ADMIN", "MANAGER"], feature: "pulse_surveys", group: "talent" },
  { path: "/training", label: "Training", labelKey: "nav.training", icon: GraduationCap, roles: ["SUPER_ADMIN", "HR_ADMIN"], feature: "training", group: "talent" },
  { path: "/my-training", label: "My Training", labelKey: "nav.myTraining", icon: GraduationCap, roles: ["EMPLOYEE"], feature: "training", group: "talent" },
  { path: "/talent", label: "Talent Map", labelKey: "nav.talent", icon: Network, roles: ["SUPER_ADMIN", "HR_ADMIN"], feature: "talent_map", group: "talent" },
  { path: "/succession", label: "Succession", labelKey: "nav.succession", icon: Crown, roles: ["SUPER_ADMIN", "HR_ADMIN"], feature: "succession", group: "talent" },

  // ── INSIGHTS ──────────────────────────────────────────────────────
  { path: "/reports", label: "Reports", labelKey: "nav.reports", icon: BarChart3, roles: ["SUPER_ADMIN", "HR_ADMIN"], feature: "advanced_reports", group: "insights" },
  { path: "/compensation/benchmarks", label: "Benchmarks", labelKey: "nav.benchmarks", icon: BarChart3, roles: ["SUPER_ADMIN", "HR_ADMIN"], feature: "compensation", group: "insights" },
  { path: "/workforce", label: "Workforce", labelKey: "nav.workforce", icon: LineChart, roles: ["SUPER_ADMIN", "HR_ADMIN"], feature: "workforce_planning", group: "insights" },
  { path: "/predict", label: "Predictions", labelKey: "nav.predict", icon: Brain, roles: ["SUPER_ADMIN", "HR_ADMIN"], feature: "predictive_analytics", group: "insights" },
  { path: "/agents", label: "Agents", labelKey: "nav.agents", icon: Bot, roles: ["SUPER_ADMIN", "HR_ADMIN"], feature: "ai_agents", group: "insights" },
  { path: "/audit-log", label: "Audit Log", labelKey: "nav.auditLog", icon: ScrollText, roles: ["SUPER_ADMIN", "HR_ADMIN"], feature: "audit_logs", group: "insights" },

  // ── COMPLIANCE & DOCS ─────────────────────────────────────────────
  { path: "/documents", label: "Documents", labelKey: "nav.documents", icon: FileText, roles: ["SUPER_ADMIN", "HR_ADMIN", "MANAGER"], group: "compliance" },
  { path: "/my-documents", label: "My Documents", labelKey: "nav.myDocuments", icon: FileText, roles: ["EMPLOYEE", "MANAGER"], group: "compliance" },
  { path: "/complaints", label: "Complaints", labelKey: "nav.complaints", icon: AlertTriangle, roles: ["SUPER_ADMIN", "HR_ADMIN"], feature: "complaints", group: "compliance" },
  // Compliance lives as a Settings tab today (no top-level page yet),
  // (Compliance Agent module is added below; the entry that used to
  // live here pointed at /settings/compliance which is the CONFIG
  // surface — wrong place to land when an admin clicks "Compliance".)
  // Legacy comment retained:
  // exposing it directly in the sidebar avoids "where is compliance"
  // confusion. Linking to the Settings tab anchor keeps the sidebar
  // entry meaningful without spinning up a new page surface.
  // Operational Compliance Agent — Sentinel's daily findings (overdue
  // docs, expiring certs, COBRA deadlines, carrier discrepancies).
  // The "Compliance" sidebar entry now points HERE — clicking it used
  // to drop admins into Settings, which was the wrong default.
  { path: "/compliance", label: "Compliance", labelKey: "nav.compliance", icon: ShieldCheck, roles: ["SUPER_ADMIN", "HR_ADMIN"], feature: "compliance", group: "compliance" },
  // COBRA continuation-of-coverage admin. Lives under the
  // Compliance group so HR doesn't have to hunt for it after
  // offboarding someone enrolled in benefits.
  { path: "/cobra", label: "COBRA", labelKey: "nav.cobra", icon: ShieldAlert, roles: ["SUPER_ADMIN", "HR_ADMIN"], group: "compliance" },
  // Reconciliation Agent — carrier invoice billing-error catcher.
  // Lives next to COBRA because both are benefits-admin chores.
  { path: "/benefits/invoices", label: "Carrier Invoices", labelKey: "nav.carrierInvoices", icon: Receipt, roles: ["SUPER_ADMIN", "HR_ADMIN"], group: "compliance" },
  { path: "/approvals", label: "Approvals", labelKey: "nav.approvals", icon: CheckSquare, roles: ["SUPER_ADMIN", "HR_ADMIN", "MANAGER"], group: "compliance" },

  // ── PINNED BOTTOM ─────────────────────────────────────────────────
  // Settings sits above the AI Assistant chip; both stick to the
  // bottom of the sidebar regardless of scroll position.
  // Platform-admin only — Lenavio staff watchdog dashboard. The
  // ``platformAdminOnly`` flag bypasses the customer ``roles`` array
  // and gates on ``user.is_platform_admin`` instead.
  { path: "/admin/ops", label: "Ops Alerts", labelKey: "nav.opsAlerts", icon: ShieldAlert, roles: ["SUPER_ADMIN", "HR_ADMIN", "MANAGER", "EMPLOYEE"], group: "platform", platformAdminOnly: true },
  { path: "/settings", label: "Settings", labelKey: "nav.settings", icon: Settings, roles: ["SUPER_ADMIN"], group: "settings" },
  { path: "/ai", label: "AI Assistant", labelKey: "nav.aiAssistant", icon: Sparkles, roles: ["SUPER_ADMIN", "HR_ADMIN", "MANAGER", "EMPLOYEE"], feature: "ai_assistant", group: "ai_assistant" },
];
